import { Component } from '@theme/component';

/**
 * « 4 Zones Stratégiques » — moteur de rendu du diagnostic visage.
 *
 * La section (Liquid) pose la photo, le titre, les libellés et un conteneur vide
 * pour les traits de rappel. Pour chaque zone, elle écrit un `.zones__scan`
 * positionné en % avec ses dimensions et la constellation choisie en data-*.
 *
 * Ce module — porté du prototype de design, sans le code d'édition
 * (drag/resize/persistance/panneau React) — se charge de :
 *   - dessiner le cadre réticule de chaque zone (voile, grilles, cadre + repères
 *     de centrage, constellation pointillée) dans un `<svg>`,
 *   - tracer le trait reliant chaque libellé à sa zone, recalculé au resize.
 *
 * L'apparence (couleur des repères, opacité du voile, affichage grilles /
 * constellations) est pilotée par les réglages de section, lus en data-* :
 *   data-red, data-frost (0–90), data-grid, data-constellations, data-ratio.
 */

const NS = 'http://www.w3.org/2000/svg';

/** @param {string} name @param {Record<string, string|number>} attrs */
const svgEl = (name, attrs) => {
  const e = document.createElementNS(NS, name);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  return e;
};

/**
 * Constellations validées par le client (cercles blancs r=7 + traits pointillés).
 * À reprendre telles quelles — l'échelle d'affichage est toujours uniforme (`meet`),
 * jamais déformée. Clé = valeur du réglage « constellation » du block.
 */
const CONSTELLATIONS = {
  front: {
    vb: [419, 369],
    svg: `
<circle cx="14" cy="12" r="7" fill="white"/>
<circle cx="161" cy="125" r="7" fill="white"/>
<circle cx="407" cy="102" r="7" fill="white"/>
<circle cx="234" cy="338" r="7" fill="white"/>
<circle cx="149" cy="258" r="7" fill="white"/>
<circle cx="81" cy="359" r="7" fill="white"/>
<path d="M409 101.499L81.4976 360.499L11.9977 9.49906L409 101.499L234.998 338.999L161.498 124.999M11.9977 9.49906L161.498 124.999M409 101.499L161.498 124.999M161.498 124.999L148.998 258.499L81.4976 360.499" stroke="white" stroke-width="2" stroke-dasharray="10 10"/>`,
  },
  pattes: {
    vb: [232, 243],
    svg: `
<circle cx="7" cy="7" r="7" fill="white"/>
<circle cx="14" cy="210" r="7" fill="white"/>
<circle cx="135" cy="163" r="7" fill="white"/>
<circle cx="81" cy="54" r="7" fill="white"/>
<circle cx="225" cy="68" r="7" fill="white"/>
<circle cx="171" cy="236" r="7" fill="white"/>
<path d="M135.5 164.5L13 210L7.53726 6.15552L81.5373 53.1555M135.5 164.5L81.5373 53.1555L226.502 67.4993L171 238L135.5 164.5ZM81.5373 53.1555L13 210" stroke="white" stroke-width="2" stroke-dasharray="10 10"/>`,
  },
  lion: {
    vb: [178, 42],
    svg: `
<circle cx="7" cy="7" r="7" fill="white"/>
<circle cx="99" cy="35" r="7" fill="white"/>
<circle cx="171" cy="7" r="7" fill="white"/>
<path d="M11.2722 9.03772L99.2722 34.0377L171.499 7.00061" stroke="white" stroke-width="2" stroke-dasharray="10 10"/>`,
  },
  cernes: {
    vb: [395, 111],
    svg: `
<circle cx="7" cy="83" r="7" fill="white"/>
<circle cx="235" cy="90" r="7" fill="white"/>
<circle cx="381" cy="7" r="7" fill="white"/>
<circle cx="388" cy="104" r="7" fill="white"/>
<path d="M11.0336 82.0012L233.033 89.0328M233.033 89.0328L381.002 7.5012L389 105.5L233.033 89.0328Z" stroke="white" stroke-width="2" stroke-dasharray="10 10"/>`,
  },
};

/** Compteur global pour des IDs de `<pattern>` uniques entre instances/zones. */
let UID = 0;

/** Délai (ms) après l'entrée dans le viewport avant de déclencher l'animation. */
const REVEAL_DELAY = 450;

class ZonesStrategiques extends Component {
  /** @type {AbortController} */
  #abort = new AbortController();
  /** @type {ResizeObserver | null} */
  #ro = null;
  /** @type {Record<string, SVGPathElement>} */
  #paths = {};
  /** @type {HTMLElement[]} */
  #scans = [];
  /** @type {MediaQueryList | null} */
  #mql = null;
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {boolean} */
  #reduced = false;
  /**
   * État par zone (rejeu indépendant à chaque entrée/sortie de SA propre visibilité).
   * @type {Map<HTMLElement, {idx:number, revealed:boolean, startTimer:number|null, antTimers:number[]}>}
   */
  #zoneInfo = new Map();

  /** Couleur des repères (cadre + grille rouge). */
  get red() {
    return this.dataset.red || '#ff3b2f';
  }

  /** Opacité du voile (0 → 1), réglée en % (0–90) côté section. */
  get frost() {
    const v = parseFloat(this.dataset.frost ?? '58');
    return (isNaN(v) ? 58 : v) / 100;
  }

  /** Couleur du voile (gris par défaut). */
  get frostColor() {
    return this.dataset.frostColor || '#7a7e89';
  }

  /** Décalage (ms) du stagger entre zones à la révélation. */
  get stagger() {
    const v = parseFloat(this.dataset.stagger ?? '240');
    return isNaN(v) || v < 0 ? 240 : v;
  }

  /** Disparition anticipée : marge haute négative (% de la hauteur d'écran). */
  get hideOffset() {
    const v = parseFloat(this.dataset.hideOffset ?? '30');
    return isNaN(v) || v < 0 ? 30 : v;
  }

  /** Si false, les zones ne se réinitialisent pas en sortie (révélées une fois). */
  get hideOnScroll() {
    return this.dataset.hideOnScroll !== 'false';
  }

  get showGrid() {
    return this.dataset.grid !== 'false';
  }

  get showConstellations() {
    return this.dataset.constellations !== 'false';
  }

  /** @param {string|undefined} value @param {number} fallback */
  #parseRatio(value, fallback) {
    const v = parseFloat(value ?? '');
    return isNaN(v) || v <= 0 ? fallback : v;
  }

  /**
   * Ratio pixel de la composition (largeur / hauteur), selon le breakpoint actif
   * (desktop ≥ 750px, sinon mobile). Encode l'aspect dans la hauteur du viewBox
   * interne pour que les ronds restent ronds et les cellules carrées, quelles que
   * soient les proportions de l'image desktop vs mobile.
   */
  get ratio() {
    const desktop = this.#parseRatio(this.dataset.ratio, 2.2361);
    if (this.#mql && !this.#mql.matches) {
      return this.#parseRatio(this.dataset.ratioMobile, desktop);
    }
    return desktop;
  }

  connectedCallback() {
    super.connectedCallback();

    this.comp = this.querySelector('.zones__composition');
    this.conSvg = this.querySelector('.zones__connectors');
    if (!this.comp || !this.conSvg) return;

    this.#mql = window.matchMedia('(min-width: 750px)');
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#scans = Array.from(this.querySelectorAll('.zones__scan'));

    // Prépare un trait + un état par zone, puis dessine les réticules.
    const count = this.#scans.length || 1;
    this.#scans.forEach((scan, idx) => {
      const id = scan.dataset.zoneId;
      const path = svgEl('path', {});
      this.conSvg.appendChild(path);
      this.#paths[id] = path;
      this.#zoneInfo.set(scan, { idx, revealed: false, startTimer: null, antTimers: [] });
      // Décale le clignotement de chaque zone sur le cycle de 3s (délai négatif
      // = phase déjà avancée) → les réticules clignotent l'un après l'autre.
      scan.style.setProperty('--zones-blink-delay', `${(-3 / count) * idx}s`);
    });
    this.#buildAll();

    const { signal } = this.#abort;
    // Observe la composition ET chaque libellé/scan : si un libellé se ré-élargit
    // (chargement de la police → l'ancrage translate(-100%) le décale), le trait
    // de rappel est recalculé pour rester collé au texte.
    this.#ro = new ResizeObserver(() => this.#redrawConnectors());
    this.#ro.observe(this.comp);
    for (const scan of this.#scans) this.#ro.observe(scan);
    for (const lab of this.querySelectorAll('.zones__label')) this.#ro.observe(lab);
    window.addEventListener('resize', this.#onResize, { passive: true, signal });
    window.addEventListener('load', this.#onResize, { signal });
    requestAnimationFrame(() => this.#redrawConnectors());
    // Reconstruit les réticules quand on franchit le breakpoint (le ratio change
    // entre image desktop et image mobile).
    this.#mql.addEventListener('change', this.#onBreakpoint, { signal });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.#redrawConnectors());

    // Anime les SVG PAR ZONE : chaque scan se révèle/se cache selon SA propre
    // visibilité → sur une section plus haute que l'écran, les zones du haut
    // disparaissent dès qu'elles sortent, sans attendre le bas de la section.
    if (this.#reduced) {
      // Pas d'anim : tout reste visible (la classe .zones--anim n'est pas posée).
      return;
    }
    // Arme l'anim : le CSS ne masque l'état initial que si le JS a tourné.
    this.classList.add('zones--anim');
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const scan = /** @type {HTMLElement} */ (e.target);
          if (e.isIntersecting) {
            this.#scheduleRevealZone(scan);
          } else if (this.hideOnScroll) {
            this.#hideZone(scan);
          } else {
            // Pas de réinitialisation : on annule juste un démarrage en attente
            // (la zone déjà révélée reste affichée).
            const info = this.#zoneInfo.get(scan);
            if (info && !info.revealed && info.startTimer != null) {
              clearTimeout(info.startTimer);
              info.startTimer = null;
            }
          }
        }
      },
      // Marge haute négative → la zone disparaît plus vite en scrollant (elle
      // sort par le haut), pilotée par le réglage. Bas à -10% → apparition inchangée.
      { rootMargin: `-${this.hideOffset}% 0px -10% 0px` }
    );
    for (const scan of this.#scans) this.#io.observe(scan);
  }

  /** (Re)dessine le réticule de chaque zone avec le ratio du breakpoint actif. */
  #buildAll() {
    for (const scan of this.#scans) {
      scan.replaceChildren(this.#buildSvg(scan));
    }
    if (!this.#reduced) {
      for (const scan of this.#scans) {
        const info = this.#zoneInfo.get(scan);
        const id = scan.dataset.zoneId;
        if (info && info.revealed) {
          // Zone déjà révélée (ex. rebuild au breakpoint) : pointillé qui défile
          // directement, sans re-jouer le tracé. (Réticule/cible restent via .is-revealed.)
          scan
            .querySelectorAll('.zones__constellation line, .zones__constellation path')
            .forEach((el) => el.classList.add('zones-ant'));
        } else {
          // Pas (encore) visible : masque l'état initial en attendant le scroll-in.
          // (Le trait de rappel est remis à l'état caché par #redrawConnectors ci-dessous.)
          this.#primeConstellation(scan.querySelector('svg'));
          this.#primeLabel(id);
        }
      }
    }
    this.#redrawConnectors();
  }

  #onBreakpoint = () => this.#buildAll();

  /**
   * État initial caché des traits/pastilles d'une constellation (avant reveal /
   * après être sorti du viewport). Réinitialise aussi le défilement.
   * @param {SVGSVGElement | null} svg
   */
  #primeConstellation(svg) {
    if (!svg) return;
    svg.querySelectorAll('.zones__constellation line, .zones__constellation path').forEach((el) => {
      el.classList.remove('zones-ant');
      let len = 0;
      try { len = el.getTotalLength(); } catch (e) {}
      if (!len) return;
      el.style.transition = 'none';
      el.style.strokeDasharray = String(len);
      el.style.strokeDashoffset = String(len);
    });
    svg.querySelectorAll('.zones__constellation circle').forEach((c) => {
      c.style.transition = 'none';
      c.style.transformBox = 'fill-box';
      c.style.transformOrigin = 'center';
      c.style.opacity = '0';
      c.style.transform = 'scale(0.2)';
    });
  }

  /** Remet le trait de rappel d'une zone à l'état caché (offset = sa longueur). */
  #hideConnector(id) {
    const p = this.#paths[id];
    if (!p) return;
    p.style.transition = 'none';
    p.style.strokeDashoffset = p.dataset.len || p.style.strokeDasharray || '9999';
  }

  /** État initial caché du libellé d'une zone (fade-up après le tracé du trait). */
  #primeLabel(id) {
    this.querySelectorAll(`.zones__label[data-zone-id="${id}"] > *`).forEach((el) => {
      el.style.transition = 'none';
      el.style.opacity = '0';
      el.style.transform = 'translateY(12px)';
    });
  }

  /** Programme la révélation d'UNE zone après un délai (+ stagger selon son index). */
  #scheduleRevealZone(scan) {
    const info = this.#zoneInfo.get(scan);
    if (!info || info.revealed || info.startTimer != null) return;
    info.startTimer = setTimeout(() => {
      info.startTimer = null;
      this.#revealZone(scan);
    }, REVEAL_DELAY + info.idx * this.stagger);
  }

  /** Ré-arme UNE zone en sortie de viewport (rejouée à sa prochaine entrée). */
  #hideZone(scan) {
    const info = this.#zoneInfo.get(scan);
    if (!info) return;
    if (info.startTimer != null) {
      clearTimeout(info.startTimer);
      info.startTimer = null;
    }
    if (!info.revealed) return;
    info.revealed = false;
    info.antTimers.forEach(clearTimeout);
    info.antTimers = [];
    const id = scan.dataset.zoneId;
    scan.classList.remove('is-revealed'); // réticule + cible repassent cachés (CSS)
    const label = this.querySelector(`.zones__label[data-zone-id="${id}"]`);
    if (label) label.classList.remove('is-revealed');
    this.#primeConstellation(scan.querySelector('svg'));
    this.#hideConnector(id);
    this.#primeLabel(id);
  }

  /** Joue l'animation de révélation d'UNE zone (rejouée à chaque entrée). */
  #revealZone(scan) {
    const info = this.#zoneInfo.get(scan);
    if (!info || info.revealed) return;
    info.revealed = true;
    info.antTimers.forEach(clearTimeout);
    info.antTimers = [];

    const id = scan.dataset.zoneId;
    const svg = scan.querySelector('svg');
    scan.classList.add('is-revealed'); // déclenche le balayage du réticule + verrouillage cible (CSS)
    if (!svg) return;

    // Délai du balayage de la grille (transition CSS sur .zones__reticle).
    const reticle = svg.querySelector('.zones__reticle');
    if (reticle) reticle.style.transitionDelay = '0ms';

    // Délai du verrouillage de la cible (transition CSS sur .zones__target).
    const target = svg.querySelector('.zones__target');
    if (target) target.style.transitionDelay = '180ms';

    // Traits de la constellation : se dessinent (dashoffset → 0), puis basculent
    // en pointillé qui défile en continu (« fourmis qui marchent »).
    const drawMs = 620;
    svg.querySelectorAll('.zones__constellation line, .zones__constellation path').forEach((el, i) => {
      const delay = 260 + i * 45;
      el.style.transition = `stroke-dashoffset ${drawMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`;
      el.style.strokeDashoffset = '0';
      // Fin du tracé → rend la main au pointillé d'origine ("10 10") et lance le défilement.
      const timer = setTimeout(() => {
        el.style.transition = '';
        el.style.strokeDasharray = '';
        el.style.strokeDashoffset = '';
        el.classList.add('zones-ant');
      }, delay + drawMs + 20);
      info.antTimers.push(timer);
    });

    // Pastilles : apparition + léger zoom.
    svg.querySelectorAll('.zones__constellation circle').forEach((c, i) => {
      const delay = 320 + i * 45;
      c.style.transition = `opacity 320ms ease ${delay}ms, transform 320ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`;
      c.style.opacity = '1';
      c.style.transform = 'scale(1)';
    });

    // Trait de rappel (label ↔ zone) : se dessine après le verrouillage de la cible.
    const lineDelay = 460, lineMs = 520;
    const con = this.#paths[id];
    if (con) {
      con.style.transition = `stroke-dashoffset ${lineMs}ms cubic-bezier(0.22, 1, 0.36, 1) ${lineDelay}ms`;
      con.style.strokeDashoffset = '0';
    }

    // Libellé : fade-up, seulement après la fin du tracé du trait.
    const labelDelay = lineDelay + lineMs;
    const label = this.querySelector(`.zones__label[data-zone-id="${id}"]`);
    if (label) label.classList.add('is-revealed');
    this.querySelectorAll(`.zones__label[data-zone-id="${id}"] > *`).forEach((el) => {
      el.style.transition = `opacity 360ms ease ${labelDelay}ms, transform 360ms cubic-bezier(0.22, 1, 0.36, 1) ${labelDelay}ms`;
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }

  // --- API publique pour l'outil de calage (éditeur de thème uniquement) ------
  // Voir `zones-calage.js` : il déplace/redimensionne les zones à la souris pour
  // relever les valeurs à recopier dans le métaobjet. Ces trois méthodes sont
  // inertes tant que cet outil n'est pas chargé (donc toujours, côté public).

  /** Recalcule les traits de rappel après un déplacement de zone ou de libellé. */
  refreshConnectors() {
    this.#redrawConnectors();
  }

  /**
   * Redessine le réticule d'UNE zone : son viewBox dépend de ses dimensions,
   * il faut donc le reconstruire après un redimensionnement.
   * @param {HTMLElement} scan
   */
  rebuildZone(scan) {
    if (!this.#zoneInfo.has(scan)) return;
    const revealed = scan.classList.contains('is-revealed');
    scan.replaceChildren(this.#buildSvg(scan));
    if (revealed) scan.classList.add('is-revealed');
    this.#redrawConnectors();
  }

  /**
   * Fige l'état révélé : plus d'apparition/disparition au scroll pendant le
   * calage (le CSS `.zones--calage` neutralise les transitions correspondantes).
   */
  freezeForCalage() {
    this.#io?.disconnect();
    this.#io = null;
    for (const scan of this.#scans) {
      const info = this.#zoneInfo.get(scan);
      if (info?.startTimer != null) {
        clearTimeout(info.startTimer);
        info.startTimer = null;
      }
      if (info) info.revealed = true;
      scan.classList.add('is-revealed');
      this.querySelector(`.zones__label[data-zone-id="${scan.dataset.zoneId}"]`)?.classList.add('is-revealed');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#ro?.disconnect();
    this.#io?.disconnect();
    for (const info of this.#zoneInfo.values()) {
      if (info.startTimer != null) clearTimeout(info.startTimer);
      info.antTimers.forEach(clearTimeout);
    }
  }

  #onResize = () => this.#redrawConnectors();

  /**
   * Construit le `<svg>` réticule d'une zone à partir de ses dimensions (% du
   * conteneur) et de sa constellation.
   * @param {HTMLElement} scan
   * @returns {SVGSVGElement}
   */
  #buildSvg(scan) {
    const onMobile = this.#mql && !this.#mql.matches;
    const w = parseFloat(onMobile ? scan.dataset.wMobile : scan.dataset.wDesktop) || 10;
    const h = parseFloat(onMobile ? scan.dataset.hMobile : scan.dataset.hDesktop) || 10;
    const uid = `z${UID++}`;
    const RED = this.red;

    const H = (100 * h) / (w * this.ratio);
    const cell = Math.max(4, 220 / w);

    const svg = svgEl('svg', { viewBox: `0 0 100 ${H.toFixed(2)}`, preserveAspectRatio: 'none' });

    const defs = svgEl('defs', {});
    const mk = (id, size, stroke, sw, op) => {
      const p = svgEl('pattern', { id, width: size, height: size, patternUnits: 'userSpaceOnUse' });
      p.appendChild(svgEl('path', { d: `M ${size} 0 L 0 0 0 ${size}`, fill: 'none', stroke, 'stroke-width': sw, opacity: op }));
      return p;
    };
    defs.appendChild(mk(`g-${uid}`, cell, '#ffffff', 0.5, 0.5));
    defs.appendChild(mk(`r-${uid}`, cell * 2, RED, 0.7, 0.5));
    svg.appendChild(defs);

    // Réticule (voile + grilles + cadre) regroupé pour pouvoir le révéler en balayage.
    const reticle = svgEl('g', { class: 'zones__reticle' });

    // Voile translucide (couleur + opacité réglables).
    reticle.appendChild(svgEl('rect', { x: 0, y: 0, width: 100, height: H, fill: this.frostColor, 'fill-opacity': this.frost, class: 'zones__veil' }));

    // Grilles blanche + rouge.
    if (this.showGrid) {
      reticle.appendChild(svgEl('rect', { x: 0, y: 0, width: 100, height: H, fill: `url(#g-${uid})` }));
      reticle.appendChild(svgEl('rect', { x: 0, y: 0, width: 100, height: H, fill: `url(#r-${uid})` }));
    }

    svg.appendChild(reticle);

    // Cible (cadre + 4 repères de centrage) : groupe à part, animé en
    // « verrouillage » de visée (CSS), indépendamment du balayage de la grille.
    const target = svgEl('g', {
      stroke: RED, 'stroke-width': 1, fill: 'none', 'vector-effect': 'non-scaling-stroke',
      class: 'zones__target',
    });
    target.appendChild(svgEl('rect', { x: 0.6, y: 0.6, width: 98.8, height: H - 1.2, 'vector-effect': 'non-scaling-stroke' }));
    const t = 6, o = 4;
    [[50, -o, 50, t], [50, H + o, 50, H - t], [-o, H / 2, t, H / 2], [100 + o, H / 2, 100 - t, H / 2]].forEach(
      ([x1, y1, x2, y2]) => target.appendChild(svgEl('line', { x1, y1, x2, y2, 'vector-effect': 'non-scaling-stroke' }))
    );
    svg.appendChild(target);

    // Constellation imbriquée, échelle uniforme (meet), centrée avec un inset.
    const constel = CONSTELLATIONS[scan.dataset.constellation];
    if (this.showConstellations && constel) {
      const [vw, vh] = constel.vb;
      const inset = 5, availW = 100 - inset * 2, availH = H - inset * 2;
      const sc = Math.min(availW / vw, availH / vh);
      const cw = vw * sc, ch = vh * sc, nx = (100 - cw) / 2, ny = (H - ch) / 2;
      const ns = svgEl('svg', {
        x: nx, y: ny, width: cw, height: ch,
        viewBox: `0 0 ${vw} ${vh}`, preserveAspectRatio: 'xMidYMid meet', overflow: 'visible',
        fill: 'none',
        class: 'zones__constellation',
      });
      ns.innerHTML = constel.svg;
      svg.appendChild(ns);
    }

    return svg;
  }

  // --- Traits de rappel : du libellé au bord de la zone, auto-suivis ---

  /** @param {Element} elm @param {DOMRect} cr */
  #relRect(elm, cr) {
    const r = elm.getBoundingClientRect();
    const x = r.left - cr.left, y = r.top - cr.top;
    return { cx: x + r.width / 2, cy: y + r.height / 2, hw: r.width / 2, hh: r.height / 2 };
  }

  /** Point de sortie du bord d'un rectangle sur la droite vers (tx, ty). */
  #exit(rect, tx, ty) {
    const dx = tx - rect.cx, dy = ty - rect.cy;
    if (!dx && !dy) return [rect.cx, rect.cy];
    const sx = dx ? rect.hw / Math.abs(dx) : 1e9, sy = dy ? rect.hh / Math.abs(dy) : 1e9;
    const s = Math.min(sx, sy);
    return [rect.cx + dx * s, rect.cy + dy * s];
  }

  #redrawConnectors() {
    if (!this.comp) return;
    const cr = this.comp.getBoundingClientRect();
    if (!cr.width) return;

    for (const scan of this.#scans) {
      const id = scan.dataset.zoneId;
      const lab = this.querySelector(`.zones__label[data-zone-id="${id}"]`);
      const path = this.#paths[id];
      if (!lab || !path) continue;

      const br = this.#relRect(scan, cr), lr = this.#relRect(lab, cr);
      const p1 = this.#exit(br, lr.cx, lr.cy), p2 = this.#exit(lr, br.cx, br.cy);
      path.setAttribute(
        'd',
        `M ${((p1[0] / cr.width) * 1000).toFixed(1)} ${((p1[1] / cr.height) * 1000).toFixed(1)} ` +
          `L ${((p2[0] / cr.width) * 1000).toFixed(1)} ${((p2[1] / cr.height) * 1000).toFixed(1)}`
      );

      // Longueur réelle du trait en pixels écran (le stroke est en non-scaling-stroke,
      // donc le pointillé se mesure en px). On l'utilise pour le draw-on.
      const len = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
      path.dataset.len = String(len);
      path.style.strokeDasharray = String(len);
      // Tant que la zone n'est pas révélée, on garde le trait caché (offset = len),
      // et on le suit si la longueur change (resize, reflow de libellé).
      const info = this.#zoneInfo.get(scan);
      if (!info || !info.revealed) {
        path.style.transition = 'none';
        path.style.strokeDashoffset = String(len);
      }
    }
  }
}

if (!customElements.get('zones-strategiques')) {
  customElements.define('zones-strategiques', ZonesStrategiques);
}
